import { FC, useState, useEffect } from 'react';
import { Container } from '@/components/ui/Container';
import { Sidebar } from './Sidebar';
import { WindowsInstallation } from './sections/WindowsInstallation';
import { MacOSInstallation } from './sections/MacOSInstallation';
import { Configuration } from './sections/Configuration';
import { SystemSelector } from '@/components/SystemSelector';


export const DocsPage: FC = () => {
  const [activeSection, setActiveSection] = useState('windows');
  const [selectedSystemId, setSelectedSystemId] = useState('');
  const [selectedSystem, setSelectedSystem] = useState<any>(null);

  // 监听滚动，自动更新活跃的章节
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['windows', 'macos', 'configuration'];
      const scrollPosition = window.scrollY + 200; // 增加偏移量，避免频繁切换

      // 从上到下检查哪个section在视窗内
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element && element.offsetTop <= scrollPosition) {
          if (activeSection !== sections[i]) {
            setActiveSection(sections[i]);
          }
          break;
        }
      }
    };

    // 防抖处理，避免频繁触发
    let timeoutId: NodeJS.Timeout;
    const debouncedHandleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 50);
    };

    window.addEventListener('scroll', debouncedHandleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
      clearTimeout(timeoutId);
    };
  }, [activeSection]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    const element = document.getElementById(section);
    if (element) {
      // 计算偏移量，确保section标题不被header遮挡
      const headerHeight = 80;
      const elementTop = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementTop,
        behavior: 'smooth'
      });
    }
  };

  const handleSystemChange = (systemId: string, systemInfo?: any) => {
    setSelectedSystemId(systemId);
    setSelectedSystem(systemInfo);
    console.log('📡 DocsPage - 系统已更改:', { systemId, systemInfo });
  };


  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <section className="pb-12 bg-white border-b border-gray-200">
        <Container>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              安装教程
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
              选择系统节点，按照步骤完成安装配置，开始使用强大的AI编程助手
            </p>
            
            {/* System Selection */}
            <div className="mb-12">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 max-w-3xl mx-auto overflow-hidden">
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-8 py-6 border-b border-gray-200">
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      1
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      选择系统节点
                    </h3>
                  </div>
                  <p className="text-center text-gray-600 mt-2">
                    选择最适合您的服务节点，获得最佳访问体验
                  </p>
                </div>
                
                <div className="px-8 py-6">
                  <SystemSelector
                    value={selectedSystemId}
                    onChange={handleSystemChange}
                    placeholder="请选择您要使用的系统节点"
                    required={true}
                  />
                  
                  {!selectedSystemId && (
                    <div className="mt-4 flex items-center justify-center space-x-2 text-gray-500">
                      <span className="text-2xl">💡</span>
                      <span className="text-sm">选择节点后，教程内容将自动适配对应的配置参数</span>
                    </div>
                  )}
                  
                  {selectedSystemId && (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
                      <div className="flex items-center justify-center space-x-2 text-green-700">
                        <span className="text-xl">✅</span>
                        <span className="font-medium">系统节点已选择</span>
                      </div>
                      <p className="text-center text-green-600 text-sm mt-1">
                        现在可以查看后续安装步骤
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            {selectedSystemId && (
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 text-sm mb-8">
                {[
                  { step: 1, label: '选择节点', active: true },
                  { step: 2, label: '选择产品', active: true },
                  { step: 3, label: '安装软件', active: false },
                  { step: 4, label: '配置环境', active: false },
                  { step: 5, label: '开始使用', active: false }
                ].map((item, index) => (
                  <div key={item.step} className="flex items-center space-x-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      item.active 
                        ? 'bg-green-500 text-white shadow-lg' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {item.active ? '✓' : item.step}
                    </div>
                    <span className={item.active ? 'text-green-600 font-medium' : 'text-gray-500'}>
                      {item.label}
                    </span>
                    {index < 4 && (
                      <div className="hidden sm:block w-6 h-0.5 bg-gray-300 ml-4"></div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Container>
      </section>

      <Container>
        {selectedSystemId ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 pt-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <Sidebar
                  activeSection={activeSection}
                  onSectionChange={handleSectionChange}
                />
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="space-y-16">
                {/* Claude 安装教程 - Section Content - 长页面布局 */}
                <section id="windows" className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                  <WindowsInstallation
                    systemId={selectedSystemId}
                    interfaceUrl={selectedSystem?.interface_url}
                  />
                </section>
                
                <section id="macos" className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                  <MacOSInstallation
                    systemId={selectedSystemId}
                    claudeApiUrl={selectedSystem?.claude_api_url}
                    openaiApiUrl={selectedSystem?.openai_api_url}
                  />
                </section>

                <section id="configuration" className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                  <Configuration
                    systemId={selectedSystemId}
                    claudeApiUrl={selectedSystem?.claude_api_url}
                    openaiApiUrl={selectedSystem?.openai_api_url}
                  />
                </section>

              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <button
                  onClick={() => {
                    const sections = ['windows', 'macos', 'configuration'];
                    const currentIndex = sections.indexOf(activeSection);
                    if (currentIndex > 0) {
                      handleSectionChange(sections[currentIndex - 1]);
                    }
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={activeSection === 'windows'}
                >
                  <span>← 上一步</span>
                </button>
                
                <div className="text-sm text-gray-500">
                  {(() => {
                    const sections = ['windows', 'macos', 'configuration'];
                    const currentIndex = sections.indexOf(activeSection);
                    return `第 ${currentIndex + 1} 步，共 3 步`;
                  })()}
                </div>
                
                <button
                  onClick={() => {
                    const sections = ['windows', 'macos', 'configuration'];
                    const currentIndex = sections.indexOf(activeSection);
                    if (currentIndex < sections.length - 1) {
                      handleSectionChange(sections[currentIndex + 1]);
                    }
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={activeSection === 'configuration'}
                >
                  <span>下一步 →</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-20 text-center">
            <div className="max-w-2xl mx-auto">
              <div className="text-gray-300 text-8xl mb-8">🚀</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                准备开始您的AI编程之旅
              </h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                为了提供最佳的安装体验，请先在上方选择您要使用的系统节点。<br />
                选择后，我们将为您展示专门适配的安装教程和配置参数。
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                <h4 className="font-semibold text-blue-900 mb-3">为什么需要选择系统节点？</h4>
                <ul className="text-sm text-blue-800 space-y-2 text-left max-w-md mx-auto">
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span>不同节点提供不同的网络访问路径</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span>每个节点有独特的配置参数</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span>确保您获得最佳的访问速度和稳定性</span>
                  </li>
                </ul>
              </div>
              
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-blue-600 text-white rounded-xl hover:from-primary-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium text-lg"
              >
                <span className="mr-2">👆</span>
                返回选择系统节点
              </button>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};
